import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.boundHandleSubmit = this.handleSubmit.bind(this)
    this.element.addEventListener('submit', this.boundHandleSubmit)
  }

  disconnect() {
    if (this.boundHandleSubmit) {
      this.element.removeEventListener('submit', this.boundHandleSubmit)
    }
  }

  getSiteKey() {
    // The server renders the site key from its configuration (layouts/_head).
    const meta = document.querySelector('meta[name="recaptcha-site-key"]')
    return meta ? meta.content : null
  }

  getTimeoutMs() {
    const timeout = parseInt(this.element.dataset.recaptchaTimeout, 10)
    return Number.isFinite(timeout) ? timeout : 2500
  }

  setSubmitting(submitButton, isSubmitting) {
    if (!submitButton) return
    submitButton.disabled = isSubmitting
  }

  appendHiddenInput(name, value) {
    let input = this.element.querySelector(`input[name="${name}"]`)
    if (!input) {
      input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      this.element.appendChild(input)
    }
    input.value = value
  }

  async fetchToken(action) {
    if (!window.grecaptcha || !grecaptcha.enterprise || !grecaptcha.enterprise.execute) {
      this.appendHiddenInput('recaptcha_error', 'unavailable')
      return null
    }

    const siteKey = this.getSiteKey()
    const timeoutMs = this.getTimeoutMs()

    const tokenPromise = new Promise((resolve, reject) => {
      try {
        grecaptcha.enterprise.ready(async () => {
          try {
            const token = await grecaptcha.enterprise.execute(siteKey, { action })
            resolve(token)
          } catch (error) {
            reject(error)
          }
        })
      } catch (error) {
        reject(error)
      }
    })

    let tokenError = null
    const guardedTokenPromise = tokenPromise.catch((error) => {
      tokenError = error
      return null
    })

    let timeoutId
    const timeoutPromise = new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve(null), timeoutMs)
    })

    const token = await Promise.race([guardedTokenPromise, timeoutPromise])
    clearTimeout(timeoutId)

    if (tokenError) {
      throw tokenError
    }

    if (!token) {
      this.appendHiddenInput('recaptcha_error', 'timeout')
    }

    return token
  }

  async handleSubmit(e) {
    e.preventDefault()

    try {
      // Show loading state if there's a submit button
      const submitButton = this.element.querySelector('[type="submit"]')
      this.setSubmitting(submitButton, true)

      const action = this.element.dataset.recaptchaAction || 'LOGIN'
      this.appendHiddenInput('recaptcha_action', action)

      const token = await this.fetchToken(action)
      if (token) {
        this.appendHiddenInput('g_recaptcha_token', token)
      }

      // Submit the form
      this.element.submit()

    } catch (error) {
      console.error('reCAPTCHA error:', error)
      this.appendHiddenInput('recaptcha_error', 'error')

      const submitButton = this.element.querySelector('[type="submit"]')
      this.setSubmitting(submitButton, false)

      // Let the server handle failure messaging instead of hanging.
      this.element.submit()
    }
  }
}
